import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { IButtonDirective, IIconComponent, ITooltipDirective, ModalService } from "@front/m19-ui";
import { Media } from "@m19-board/shared/media/media";
import { MediaType } from "@front/m19-api-client";
import { isVideo } from "@m19-board/shared/media/media.utils";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  MediaPreviewModalComponent,
  MediaPreviewModalData,
} from "@m19-board/shared/media/media-preview-modal/media-preview-modal.component";

@Component({
  selector: "app-media-card",
  imports: [IButtonDirective, TranslocoDirective, ITooltipDirective, IIconComponent],
  template: `<div
    class="group hover:border-main-500 flex h-full w-full min-w-0 cursor-pointer flex-col items-center justify-center rounded border border-gray-100 p-2"
    [class.checkbox-card-selected]="isSelected()"
    *transloco="let t"
  >
    @if (!isVideo()) {
      <img class="max-h-9/10 max-w-full object-contain" [src]="media().url" />
      <div class="mt-2 flex w-full min-w-0 flex-col p-1">
        <div class="truncate text-xs font-semibold">{{ media().name }}</div>
        @if (media().width && media().height) {
          <div class="text-muted-foreground text-xs">{{ media().width }}x{{ media().height }}</div>
        }
      </div>
    } @else {
      @defer (on viewport) {
        <video #video class="max-h-9/10 max-w-full object-contain" (loadedmetadata)="onLoadedMetadata(video)">
          <source [src]="media().url + '#t=0.1'" type="video/mp4" />
        </video>
      } @placeholder {
        <div class="flex h-full items-center justify-center">
          <div class="animate-pulse text-sm text-gray-500">{{ t("common.loading") }}</div>
        </div>
      }
      <div class="mt-2 flex w-full items-center justify-between">
        <div class="flex min-w-0 flex-col">
          <div class="max-w-full text-xs font-semibold break-words">{{ media().name }}</div>
          @if (durationSeconds(); as duration) {
            <div class="text-xs text-gray-500">{{ duration }}s</div>
          }
          @if (media().width && media().height) {
            <div class="text-muted-foreground text-xs">{{ media().width }}x{{ media().height }}</div>
          }
        </div>
        <button
          iButton
          variant="secondary"
          [iTooltip]="t('media-input.preview_video')"
          (click)="openMediaPreview(media()); $event.stopPropagation()"
        >
          <IIcon name="eye" />
        </button>
      </div>
    }
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaCardComponent {
  private readonly modalService = inject(ModalService);
  media = input.required<Media>();
  mediaType = input.required<MediaType>();
  isSelected = input<boolean>(false);

  isVideo = computed(() => isVideo(this.mediaType()));
  // Duration (seconds) of the video, read from its metadata once the element loads.
  readonly durationSeconds = signal<number | undefined>(undefined);

  onLoadedMetadata(video: HTMLVideoElement) {
    this.durationSeconds.set(Number.isFinite(video.duration) ? Math.round(video.duration) : undefined);
  }

  openMediaPreview(media: Media) {
    this.modalService.openModal<MediaPreviewModalData, void>(MediaPreviewModalComponent, {
      modalTitle: media.name,
      data: {
        mediaSource: media.url ?? "",
        mediaType: this.mediaType(),
      },
    });
  }
}
